import { getLoopSelectedAtoms, standardDrawing } from '@moleculeone/draw';
import { Vector } from '@moleculeone/types';
import { V4, expandToMinSize } from '@moleculeone/utils';
import { DrawnMol, Mol } from '@retro-ui/api-contract';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DASHED,
  WEDGE,
  clearChiral,
  clearGeometry,
  clearMol,
  flipBond,
  flipGeometry,
  flipMol,
  molMouseEvent,
  onKeyEvent,
  parseName,
  parseSmiles,
  pickElement,
  redrawMol,
  rotateAroundBond,
  setChiral,
  setFragmentAtomMode,
  setFragmentBondMode,
  setGeometry,
  setMol,
  setMols,
  setPlainMode,
} from '../utils/molecule-object/molecule-object.actions';
import { EditorMode, EditorModeType } from '../utils/molecule-object/reducer/mode-types';
import { preview } from '../utils/molecule-object/reducer/preview';

export type Space = {
  mol: Mol;
  drawnMol: DrawnMol;
  loop: any;
  svgPreview: DrawnMol;
  viewport: {
    min: Vector;
    max: Vector;
  };
  onMouseEvent: React.MouseEventHandler;
  redraw: () => void;
  clear: () => void;
  locationAnalogPromptNodes?: React.ReactNode;
};
export interface CompoundEditorState {
  spaces: Space[];
  lastMolsUpdate: Date;
  activeSpaceIndex: number;
  mode: EditorMode;
}
export type MolEvent = any;

export interface CompoundEditorActions {
  onPickElement: (event: MolEvent, element: string) => void;
  onChangeDir: (bondIdx: number) => void;
  onClearChirality: (bondIdx: number) => void;
  onSetChirality: (bondIdx: number, type: string) => void;
  onSetWedge: (bondIdx: number) => void;
  onSetDashed: (bondIdx: number) => void;
  onSetGeometry: (bondIdx: number) => void;
  onClearGeometry: (bondIdx: number) => void;
  onChangeGeometry: (bondIdx: number) => void;
  onRotateBond: (bondIdx: number) => void;
  onFlipMol: (axis: Vector) => void;
  onSelectMode: (mode: string) => void;
  onFragmentAtomMode: (fragment: any, idx: string) => void;
  onFragmentBondMode: (fragment: any, idx: string) => void;
  onParseSmiles: (smiles: string) => void;
  onParseName: (name: string) => void;
  onKeyDown: (event: MolEvent) => void;
  onKeyUp: (event: MolEvent) => void;
  redraw: () => void;
  clear: () => void;
  setMol: (mol: Mol, molIndex: number) => void;
  setMols: (mols: Mol[], initial?: boolean) => void;
}

export type CompoundEditorRedux = CompoundEditorState & {
  // viewport: any;
  actions: CompoundEditorActions;
};

function notInputEvent(event: KeyboardEvent) {
  if (event.target instanceof HTMLInputElement) {
    return false;
  }
  if (event.target instanceof HTMLTextAreaElement) {
    return false;
  }
  return true;
}
function notButtonEvent(event: MouseEvent) {
  if (event.target instanceof HTMLButtonElement) {
    return false;
  }
  if (event.target && (event.target as HTMLElement).closest('button')) {
    return false;
  }
  return true;
}

export function useCompoundEditor(): CompoundEditorRedux {
  const dispatch = useDispatch();

  const { spaces, lastMolsUpdate, activeSpaceIndex, mode }: CompoundEditorState = useSelector((state: any) => {
    const { mols, lastMolsUpdate, mode, selected, mouse } = state.moleculeObject;

    const spaces = mols.map((mol, molIndex) => {
      let svgPreview: DrawnMol | null = null;
      let loop = [];

      if (mouse.molIndex === molIndex) {
        if (mode.type === EditorModeType.SELECT_MODE) {
          loop = mode.data.loop;
        }
        const molPreview = preview(state.moleculeObject);
        if (molPreview != null) {
          svgPreview = standardDrawing(molPreview, []) as DrawnMol;
        }
      }

      const selectedAtoms = [...selected[molIndex], ...getLoopSelectedAtoms(mol, loop)];
      const drawnMol = standardDrawing(mol, selectedAtoms) as DrawnMol;

      const bbox = drawnMol.bbox;
      const viewport = expandToMinSize(bbox, V4.vect(600, 600));

      return {
        mol,
        drawnMol,
        loop,
        svgPreview,
        viewport,
        onMouseEvent: (event, molEvent) => {
          if (molEvent && notButtonEvent(event)) {
            dispatch(molMouseEvent(molEvent, molIndex));
          }
        },
        redraw: () => {
          dispatch(redrawMol(molIndex));
        },
        clear: () => {
          dispatch(clearMol(molIndex));
        },
      };
    });

    return {
      spaces,
      lastMolsUpdate,
      activeSpaceIndex: mouse.molIndex ?? 0,
      mode,
    };
  });

  // TODO implement spaces viewport unifying logic
  // let maxViewport = spaces[0].viewport;
  // const firstViewportSum = maxViewport.max[0] + maxViewport.max[1];
  // for (let i = 1; i < spaces.length; i++) {
  //   const viewport = spaces[i].viewport;
  //   const sum = viewport.max[0] + viewport.max[1];
  //   if (sum > firstViewportSum) {
  //     maxViewport = viewport;
  //   }
  // }

  const actions: CompoundEditorActions = useMemo(
    () => ({
      onPickElement: (event, element) => {
        dispatch(pickElement(event, element));
      },
      onChangeDir: bondIdx => {
        dispatch(flipBond(bondIdx));
      },
      onClearChirality: bondIdx => {
        dispatch(clearChiral(bondIdx));
      },
      onSetChirality: (bondIdx, type) => {
        dispatch(setChiral(bondIdx, type));
      },
      onSetWedge: bondIdx => {
        dispatch(setChiral(bondIdx, WEDGE));
      },
      onSetDashed: bondIdx => {
        dispatch(setChiral(bondIdx, DASHED));
      },
      onSetGeometry: bondIdx => {
        dispatch(setGeometry(bondIdx));
      },
      onClearGeometry: bondIdx => {
        dispatch(clearGeometry(bondIdx));
      },
      onChangeGeometry: bondIdx => {
        dispatch(flipGeometry(bondIdx));
      },
      onRotateBond: bondIdx => {
        dispatch(rotateAroundBond(bondIdx));
      },
      onFlipMol: axis => {
        dispatch(flipMol(axis));
      },
      onSelectMode: mode => dispatch(setPlainMode(mode)),
      onFragmentAtomMode: (fragment, idx) => {
        dispatch(setFragmentAtomMode(fragment, idx));
      },
      onFragmentBondMode: (fragment, idx) => {
        dispatch(setFragmentBondMode(fragment, idx));
      },
      onParseSmiles: smiles => {
        dispatch(parseSmiles(smiles));
      },
      onParseName: name => {
        dispatch(parseName(name));
      },
      onKeyDown: event => {
        if (notInputEvent(event)) {
          dispatch(onKeyEvent(event, 'DOWN'));
        }
      },
      onKeyUp: event => {
        if (notInputEvent(event)) {
          dispatch(onKeyEvent(event, 'UP'));
        }
      },
      redraw: () => {
        dispatch(redrawMol());
      },
      clear: () => {
        dispatch(clearMol());
      },
      setMol: (mol, molIndex) => {
        dispatch(setMol({ mol, molIndex }));
      },
      setMols: (mols, initial) => {
        dispatch(setMols({ mols, initial }));
      },
    }),
    []
  );

  return {
    spaces,
    lastMolsUpdate,
    // viewport: maxViewport,
    activeSpaceIndex,
    actions,
    mode,
  };
}
